import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { deepSeekService } from '../../services/deepSeekService';
import { generateReply, type ChatFn } from '../../services/assistant/engine';
import { buildSnapshot } from '../../services/assistant/snapshot';
import { ensureAssistantData, readAssistantData } from '../../services/assistant/storeData';
import { pageLabelForPath } from '../../services/assistant/navTargets';
import { toIsoDay } from '../../services/assistant/format';
import type { PortfolioSnapshot } from '../../services/assistant/types';
import { useAssistantStore } from './assistantStore';

const backendChat: ChatFn = (messages, context) => deepSeekService.chat(messages, context);

function currentSnapshot(): PortfolioSnapshot {
  return buildSnapshot(readAssistantData(), toIsoDay(new Date()));
}

/** Loads the landlord's data (once) and returns a snapshot for the welcome card. */
export function usePortfolioSnapshot(): PortfolioSnapshot | null {
  const [snapshot, setSnapshot] = useState<PortfolioSnapshot | null>(null);
  useEffect(() => {
    let cancelled = false;
    void ensureAssistantData().then(() => {
      if (!cancelled) setSnapshot(currentSnapshot());
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return snapshot;
}

export function useAssistant(chat: ChatFn = backendChat) {
  const messages = useAssistantStore((s) => s.messages);
  const awaiting = useAssistantStore((s) => s.awaiting);
  const clear = useAssistantStore((s) => s.clear);
  const { pathname } = useLocation();

  const send = useCallback(
    async (text: string) => {
      const question = text.trim();
      const store = useAssistantStore.getState();
      if (!question || store.awaiting) return;

      const epoch = store.epoch;
      // Fallback notices are not real model turns: keep them out of history.
      const history = store.messages
        .filter((m) => m.source !== 'fallback')
        .map(({ role, content }) => ({ role, content }));
      store.addMessage({ role: 'user', content: question });
      store.setAwaiting(true);

      try {
        await ensureAssistantData();
        const reply = await generateReply({
          question,
          history,
          snapshot: currentSnapshot(),
          pageLabel: pageLabelForPath(pathname),
          chat,
        });
        if (useAssistantStore.getState().epoch === epoch) {
          useAssistantStore.getState().addMessage({ role: 'assistant', ...reply });
        }
      } catch (error) {
        console.error('Assistant failed to answer:', error);
        if (useAssistantStore.getState().epoch === epoch) {
          useAssistantStore.getState().addMessage({
            role: 'assistant',
            content: 'Sorry, something went wrong while reading your data. Please try again.',
            source: 'fallback',
          });
        }
      } finally {
        if (useAssistantStore.getState().epoch === epoch) {
          useAssistantStore.getState().setAwaiting(false);
        }
      }
    },
    [chat, pathname],
  );

  return { messages, awaiting, send, clear };
}
